import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Thermometer, 
  CheckCircle, 
  AlertTriangle, 
  Clock,
  ChevronDown,
  ChevronRight,
  Calendar,
  User,
  Pencil,
  Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TemperatureControl = () => {
  const { user } = useAuth();
  const [measuringPoints, setMeasuringPoints] = useState([]);
  const [readingsByDay, setReadingsByDay] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedDays, setExpandedDays] = useState({});
  
  // Edit Dialog State
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingReading, setEditingReading] = useState(null);
  const [editTemp, setEditTemp] = useState('');
  const [editComment, setEditComment] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Get measuring points - use admin endpoint for admin, regular for employee
      let pointsEndpoint;
      if (user?.role === 'admin') {
        pointsEndpoint = `${API}/admin/temperature/measuring-points`;
      } else {
        // Employee: use first location_id from the array
        const locationId = user?.location_ids?.[0];
        if (!locationId) {
          toast.error('Kein Standort zugewiesen');
          setLoading(false);
          return;
        }
        pointsEndpoint = `${API}/temperature/measuring-points?location_id=${locationId}`;
      }
      
      const pointsRes = await axios.get(pointsEndpoint);
      const activePoints = pointsRes.data.filter(p => p.status === 'active');
      setMeasuringPoints(activePoints);

      // Get readings for last 5 days
      const readingsRes = await axios.get(`${API}/admin/temperature/readings-by-day?days=5`);
      setReadingsByDay(readingsRes.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const toggleDay = (dateKey) => {
    setExpandedDays(prev => ({ ...prev, [dateKey]: !prev[dateKey] }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const dayBefore = new Date(today);
    dayBefore.setDate(dayBefore.getDate() - 2);

    if (date.toDateString() === today.toDateString()) {
      return 'Heute';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Gestern';
    } else if (date.toDateString() === dayBefore.toDateString()) {
      return 'Vorgestern';
    } else {
      return date.toLocaleDateString('de-DE', { 
        weekday: 'long',
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric' 
      });
    }
  };

  const getReadingForPoint = (pointId, dayReadings) => {
    if (!dayReadings) return null;
    return dayReadings.find(r => r.measuring_point_id === pointId);
  };

  const getStatus = (reading, point) => {
    if (!reading) {
      return { status: 'pending', label: 'Ausstehend', color: 'bg-yellow-100 text-yellow-800', icon: Clock };
    }
    if (reading.is_alarm) {
      return { status: 'alarm', label: 'ALARM', color: 'bg-red-100 text-red-800', icon: AlertTriangle };
    }
    return { status: 'ok', label: 'OK', color: 'bg-green-100 text-green-800', icon: CheckCircle };
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Check if user can edit this reading
  const canEditReading = (reading) => {
    if (!reading) return false;
    // Both admin and employee can edit temperature readings
    return user?.role === 'admin' || user?.role === 'employee';
  };

  // Open edit dialog
  const handleEditClick = (reading, point) => {
    setEditingReading({ ...reading, point });
    setEditTemp(String(reading.temperature));
    setEditComment(reading.comment || '');
    setEditDialogOpen(true);
  };

  // Save edited reading
  const handleSaveEdit = async () => {
    if (!editingReading || !editTemp) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/temperature/${editingReading.id}`, {
        temperature: parseFloat(editTemp),
        comment: editComment || null
      });
      
      toast.success('Temperatur erfolgreich aktualisiert');
      setEditDialogOpen(false);
      setEditingReading(null);
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  // Generate last 5 days
  const getLast5Days = () => {
    const days = [];
    for (let i = 0; i < 5; i++) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      days.push(date.toISOString().split('T')[0]);
    }
    return days;
  };

  const renderDayTable = (dateKey, isToday = false) => {
    const dayReadings = readingsByDay[dateKey] || [];
    
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50 dark:bg-gray-800">
              <th className="text-left p-3 font-medium">Messpunkt</th>
              <th className="text-left p-3 font-medium">Soll-Wert</th>
              <th className="text-left p-3 font-medium">Ist-Wert</th>
              <th className="text-left p-3 font-medium">Status</th>
              <th className="text-left p-3 font-medium">Zeit / Mitarbeiter</th>
              <th className="text-left p-3 font-medium w-16">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {measuringPoints.map((point) => {
              const reading = getReadingForPoint(point.id, dayReadings);
              const statusInfo = getStatus(reading, point);
              const StatusIcon = statusInfo.icon;
              const showEditButton = canEditReading(reading);
              
              return (
                <tr key={point.id} className="border-b hover:bg-gray-50 dark:bg-gray-800">
                  <td className="p-3 font-medium">{point.name}</td>
                  <td className="p-3 text-gray-600 dark:text-gray-400">
                    {point.target_temp}°C (±{point.tolerance}°C)
                  </td>
                  <td className="p-3">
                    {reading ? (
                      <span className={`font-bold ${reading.is_alarm ? 'text-red-600' : 'text-green-600'}`}>
                        {reading.temperature}°C
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="p-3">
                    <Badge className={statusInfo.color}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {statusInfo.label}
                    </Badge>
                  </td>
                  <td className="p-3 text-gray-500 text-xs">
                    {reading ? (
                      <div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatTime(reading.created_at)}
                        </div>
                        <div className="flex items-center gap-1 mt-1">
                          <User className="h-3 w-3" />
                          {reading.recorded_by_name || reading.recorded_by || 'Unbekannt'}
                        </div>
                        {reading.comment && (
                          <div className="mt-1 text-xs text-gray-400 italic">
                            "{reading.comment}"
                          </div>
                        )}
                      </div>
                    ) : (
                      <span>—</span>
                    )}
                  </td>
                  <td className="p-3">
                    {showEditButton && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditClick(reading, point)}
                        className="h-8 w-8 p-0 hover:bg-blue-100"
                        title="Bearbeiten"
                      >
                        <Pencil className="h-4 w-4 text-blue-600" />
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  const days = getLast5Days();
  const todayKey = days[0];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}}>
            Kontrolle der Temperatur
          </h1>
          <p className="text-muted-foreground mt-2">
            Übersicht der Temperaturmessungen der letzten 5 Tage
          </p>
        </div>

        {/* Today - Always Open */}
        <Card className="border-2 border-blue-200">
          <CardHeader className="bg-blue-50">
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-600" />
              <span className="text-blue-800">
                {formatDate(todayKey)} ({new Date(todayKey).toLocaleDateString('de-DE')})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {renderDayTable(todayKey, true)}
          </CardContent>
        </Card>

        {/* Previous Days - Accordion */}
        <div className="space-y-2">
          {days.slice(1).map((dateKey) => {
            const isExpanded = expandedDays[dateKey];
            const dayReadings = readingsByDay[dateKey] || [];
            const completedCount = measuringPoints.filter(p => 
              dayReadings.some(r => r.measuring_point_id === p.id)
            ).length;
            const alarmCount = dayReadings.filter(r => r.is_alarm).length;
            
            return (
              <Card key={dateKey} className="overflow-hidden">
                <button
                  onClick={() => toggleDay(dateKey)}
                  className="w-full p-4 flex items-center justify-between hover:bg-gray-50 dark:bg-gray-800 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-gray-500" />
                    )}
                    <Calendar className="h-5 w-5 text-gray-400" />
                    <span className="font-medium">
                      {formatDate(dateKey)} ({new Date(dateKey).toLocaleDateString('de-DE')})
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-gray-100 dark:bg-gray-800">
                      {completedCount}/{measuringPoints.length} gemessen
                    </Badge>
                    {alarmCount > 0 && (
                      <Badge variant="destructive">
                        {alarmCount} Alarm{alarmCount > 1 ? 'e' : ''}
                      </Badge>
                    )}
                  </div>
                </button>
                
                {isExpanded && (
                  <div className="border-t">
                    {renderDayTable(dateKey, false)}
                  </div>
                )}
              </Card>
            );
          })}
        </div>

        {/* Legend */}
        <Card className="bg-gray-50 dark:bg-gray-800">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Badge className="bg-green-100 text-green-800">
                  <CheckCircle className="h-3 w-3 mr-1" /> OK
                </Badge>
                <span className="text-gray-600 dark:text-gray-400">Innerhalb Toleranz</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-red-100 text-red-800">
                  <AlertTriangle className="h-3 w-3 mr-1" /> ALARM
                </Badge>
                <span className="text-gray-600 dark:text-gray-400">Außerhalb Toleranz</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-yellow-100 text-yellow-800">
                  <Clock className="h-3 w-3 mr-1" /> Ausstehend
                </Badge>
                <span className="text-gray-600 dark:text-gray-400">Noch nicht gemessen</span>
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <Pencil className="h-4 w-4 text-blue-600" />
                <span className="text-gray-600 dark:text-gray-400">Klicken zum Bearbeiten</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Thermometer className="h-5 w-5 text-blue-600" />
              Temperatur bearbeiten
            </DialogTitle>
          </DialogHeader>
          
          {editingReading && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="font-medium">{editingReading.point?.name}</p>
                <p className="text-sm text-gray-500">
                  Soll: {editingReading.point?.target_temp}°C (±{editingReading.point?.tolerance}°C)
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-temp">Temperatur (°C) *</Label>
                <Input
                  id="edit-temp"
                  type="number"
                  step="0.1"
                  value={editTemp}
                  onChange={(e) => setEditTemp(e.target.value)}
                  placeholder="z.B. 4.5"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-comment">Kommentar (optional)</Label>
                <Input
                  id="edit-comment"
                  value={editComment}
                  onChange={(e) => setEditComment(e.target.value)}
                  placeholder="Grund für Änderung..."
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving || !editTemp}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Speichern...
                </>
              ) : (
                'Speichern'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};
